import { useState, useRef } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Chip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DescriptionIcon from '@mui/icons-material/Description';
import { smoothSlideIn, refinedGlow, checkmarkBurst, diagonalFlow, springEasing } from '../theme/animations';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ACCEPTED_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel', // .xls
  'text/csv', // .csv
];

/**
 * UploadSection - Two-Step Process for Template Download & Upload
 *
 * Guides users through downloading the EAS template and uploading
 * their completed equipment data with clear visual feedback.
 */
export default function UploadSection({ onFileUpload, onError, uploadError }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    // Check file type
    if (!ACCEPTED_TYPES.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      onError('Please upload a valid file (.xlsx, .xls, or .csv)');
      return false;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      onError(`File size must be less than ${MAX_FILE_SIZE / 1024 / 1024}MB`);
      return false;
    }

    return true;
  };

  const handleFileSelect = (file) => {
    if (!file) return;

    if (validateFile(file)) {
      setSelectedFile(file);
      setIsProcessing(true);

      // Simulate processing delay for UX feedback
      setTimeout(() => {
        setIsProcessing(false);
        onFileUpload(file);
      }, 500);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Step 1: Download Template */}
      <Grid item xs={12} md={6}>
        <Card
          sx={{
            height: '100%',
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(26, 31, 37, 1) 0%, rgba(20, 20, 20, 1) 100%)'
                : 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            border: '2px solid',
            borderColor: 'divider',
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden',
            animation: `${smoothSlideIn} 0.6s ease-out`,
            transition: `all 0.3s ${springEasing}`,
            '&:hover': {
              borderColor: 'primary.light',
              boxShadow: (theme) =>
                theme.palette.mode === 'dark'
                  ? '0 8px 24px rgba(160, 206, 78, 0.15)'
                  : '0 8px 24px rgba(124, 179, 66, 0.12)',
              transform: 'translateY(-2px)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: (theme) =>
                `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            },
          }}
        >
          <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Step Badge */}
            <Chip
              label="Step 1"
              size="small"
              sx={{
                alignSelf: 'flex-start',
                mb: 2,
                fontWeight: 600,
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                color: (theme) => theme.palette.primary.contrastText,
              }}
            />

            {/* Icon */}
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: 2,
                background: (theme) =>
                  theme.palette.mode === 'dark'
                    ? `radial-gradient(circle, ${theme.palette.primary.main}15, transparent)`
                    : `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.primary.light}08)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <DownloadIcon sx={{ fontSize: 36, color: 'primary.main' }} />
            </Box>

            {/* Content */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 1.5,
                letterSpacing: '-0.01em',
              }}
            >
              Download Template
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, lineHeight: 1.7, flex: 1 }}
            >
              Get our pre-formatted Excel template with example data. Fill in your equipment details:
              type, tonnage per unit, age, operating hours, and electricity rate.
            </Typography>

            {/* Download Button */}
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<DownloadIcon />}
              href="/templates/eas-template.xlsx"
              download
              sx={{
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                boxShadow: (theme) =>
                  theme.palette.mode === 'dark'
                    ? '0 4px 12px rgba(197, 232, 125, 0.25)'
                    : '0 4px 12px rgba(124, 179, 66, 0.3)',
                transition: `all 0.3s ${springEasing}`,
                '&:hover': {
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.darker})`,
                  boxShadow: (theme) =>
                    theme.palette.mode === 'dark'
                      ? '0 6px 16px rgba(197, 232, 125, 0.35)'
                      : '0 6px 16px rgba(124, 179, 66, 0.4)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Download EAS Template
            </Button>

            {/* Helper Text */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                mt: 2,
                display: 'block',
                textAlign: 'center',
                fontStyle: 'italic',
              }}
            >
              Opens in Excel, Google Sheets, or Numbers
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Step 2: Upload Completed Template */}
      <Grid item xs={12} md={6}>
        <Card
          sx={{
            height: '100%',
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(20, 20, 20, 1) 0%, rgba(26, 31, 37, 1) 100%)'
                : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '2px solid',
            borderColor: uploadError ? 'error.main' : selectedFile ? 'success.main' : 'divider',
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden',
            animation: `${smoothSlideIn} 0.6s ease-out`,
            animationDelay: '0.1s',
            animationFillMode: 'backwards',
            transition: `all 0.3s ${springEasing}`,
            '&:hover': {
              borderColor: selectedFile ? 'success.main' : 'primary.light',
              boxShadow: (theme) =>
                selectedFile
                  ? '0 8px 24px rgba(78, 205, 196, 0.15)'
                  : theme.palette.mode === 'dark'
                  ? '0 8px 24px rgba(160, 206, 78, 0.15)'
                  : '0 8px 24px rgba(124, 179, 66, 0.12)',
              transform: 'translateY(-2px)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: (theme) =>
                uploadError
                  ? `linear-gradient(90deg, ${theme.palette.error.main}, ${theme.palette.error.light})`
                  : selectedFile
                  ? `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`
                  : `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            },
          }}
        >
          <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Step Badge */}
            <Chip
              label="Step 2"
              size="small"
              sx={{
                alignSelf: 'flex-start',
                mb: 2,
                fontWeight: 600,
                background: (theme) =>
                  uploadError
                    ? `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.light})`
                    : selectedFile
                    ? `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.light})`
                    : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                color: (theme) =>
                  uploadError ? theme.palette.error.contrastText : selectedFile ? theme.palette.success.contrastText : theme.palette.primary.contrastText,
              }}
            />

            {/* Icon */}
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: 2,
                background: (theme) =>
                  uploadError
                    ? theme.palette.mode === 'dark'
                      ? `radial-gradient(circle, ${theme.palette.error.main}20, transparent)`
                      : `linear-gradient(135deg, ${theme.palette.error.main}12, ${theme.palette.error.light}08)`
                    : selectedFile
                    ? theme.palette.mode === 'dark'
                      ? `radial-gradient(circle, ${theme.palette.success.main}20, transparent)`
                      : `linear-gradient(135deg, ${theme.palette.success.main}12, ${theme.palette.success.light}08)`
                    : theme.palette.mode === 'dark'
                    ? `radial-gradient(circle, ${theme.palette.primary.main}15, transparent)`
                    : `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.primary.light}08)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                animation: selectedFile ? `${checkmarkBurst} 0.6s ${springEasing}` : 'none',
              }}
            >
              {uploadError ? (
                <ErrorIcon sx={{ fontSize: 36, color: 'error.main' }} />
              ) : selectedFile ? (
                <CheckCircleIcon sx={{ fontSize: 36, color: 'success.main' }} />
              ) : (
                <UploadFileIcon sx={{ fontSize: 36, color: 'primary.main' }} />
              )}
            </Box>

            {/* Content */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 1.5,
                letterSpacing: '-0.01em',
              }}
            >
              Upload Completed Template
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, lineHeight: 1.7 }}
            >
              Upload your completed EAS file to calculate detailed ROI metrics and CFO-level
              financial analysis.
            </Typography>

            {/* Upload Zone */}
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !isProcessing && fileInputRef.current?.click()}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed',
                borderColor: isDragging
                  ? 'primary.main'
                  : selectedFile
                  ? 'success.main'
                  : 'divider',
                borderRadius: 2,
                p: 3,
                cursor: isProcessing ? 'wait' : 'pointer',
                background: (theme) =>
                  isDragging
                    ? theme.palette.mode === 'dark'
                      ? `repeating-linear-gradient(
                          45deg,
                          ${theme.palette.primary.main}08,
                          ${theme.palette.primary.main}08 10px,
                          transparent 10px,
                          transparent 20px
                        )`
                      : `repeating-linear-gradient(
                          45deg,
                          ${theme.palette.primary.main}06,
                          ${theme.palette.primary.main}06 10px,
                          transparent 10px,
                          transparent 20px
                        )`
                    : selectedFile
                    ? theme.palette.mode === 'dark'
                      ? `${theme.palette.success.main}10`
                      : `${theme.palette.success.main}08`
                    : 'transparent',
                backgroundSize: '40px 40px',
                transition: 'all 0.3s ease',
                animation: isDragging ? `${refinedGlow} 1.5s infinite, ${diagonalFlow} 20s linear infinite` : 'none',
                '&:hover': {
                  borderColor: selectedFile ? 'success.main' : 'primary.main',
                  background: (theme) =>
                    selectedFile
                      ? theme.palette.mode === 'dark'
                        ? `${theme.palette.success.main}12`
                        : `${theme.palette.success.main}10`
                      : theme.palette.mode === 'dark'
                      ? `${theme.palette.primary.main}08`
                      : `${theme.palette.primary.main}06`,
                },
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />

              {isProcessing ? (
                <>
                  <CircularProgress size={40} sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Processing file...
                  </Typography>
                </>
              ) : selectedFile ? (
                <>
                  <DescriptionIcon
                    sx={{
                      fontSize: 48,
                      color: uploadError ? 'error.main' : 'success.main',
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: uploadError ? 'error.main' : 'success.main',
                      mb: 0.5,
                      textAlign: 'center',
                    }}
                  >
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </Typography>
                  {uploadError && (
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{
                        mt: 2,
                        textAlign: 'center',
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        background: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(244, 67, 54, 0.1)'
                            : 'rgba(244, 67, 54, 0.08)',
                      }}
                    >
                      {uploadError}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 2, textAlign: 'center' }}
                  >
                    Click to select a different file
                  </Typography>
                </>
              ) : (
                <>
                  <UploadFileIcon
                    sx={{
                      fontSize: 48,
                      color: isDragging ? 'primary.main' : 'action.active',
                      mb: 2,
                      transition: 'all 0.3s ease',
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      mb: 0.5,
                      textAlign: 'center',
                    }}
                  >
                    {isDragging ? 'Drop file here' : 'Click or drag file to upload'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                    Accepts .xlsx, .xls, and .csv files up to 50MB
                  </Typography>
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
